/**
 * Role / plan awareness for the navigation.
 *
 *  - `adminOnly` items are HIDDEN for non-admins (owner / admin /
 *    platform_superadmin keep them).
 *  - `feature` items stay visible but are flagged `locked` when the tenant's plan
 *    excludes the feature (lock badge; the page itself shows the upgrade card via
 *    <PlanGate>). While entitlements load nothing is locked (no flash).
 */

import { useCallback } from 'react';

import type { NavItem } from '@/app/layouts/navCatalog';
import { useEntitlement } from '@/features/entitlements';
import { useRoles } from '@/routes/settings/useRoles';

export interface NavAccess {
  isVisible: (item: NavItem) => boolean;
  isLocked: (item: NavItem) => boolean;
}

export function useNavAccess(): NavAccess {
  const { isOwnerOrAdmin, isSuperadmin } = useRoles();
  const { hasFeature, loaded } = useEntitlement();
  const canSeeAdmin = isOwnerOrAdmin || isSuperadmin;

  const isVisible = useCallback(
    (item: NavItem) => !item.adminOnly || canSeeAdmin,
    [canSeeAdmin],
  );
  const isLocked = useCallback(
    (item: NavItem) => Boolean(item.feature) && loaded && !hasFeature(item.feature as string, true),
    // hasFeature is recreated each render but is a pure read of the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loaded, hasFeature],
  );
  return { isVisible, isLocked };
}
